import {
  getAllIds,
  getCountByStatus,
  useGetAllArtworks,
} from "@/api/artwork/artwork";
import {
  useDeleteEnsembles,
  useRemoveArtworksFromEnsembles,
  useUpdateTemporarySet,
} from "@/api/ensemble/ensemble";
import ArtworkPrintDrawer from "@/components/artwork/ArtworkPrintDrawer";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import { NewArtworkJourney } from "@/components/artwork/NewArtworkJourney";
import DeleteSetModal from "@/components/ensemble/DeleteSetModal";
import CreateArtworkSet from "@/components/ensemble/LinkSetsAndArtworks";
import NewArtworkSetModal from "@/components/ensemble/NewArtworkSetModal";
import StatusTableTab from "@/components/StatusTableTab";
import { DashboardListGridLayout } from "@/components/dashboard/DashboardListGridLayout";
import { GridCardWithActions } from "@/components/links/GridCardWithActions";
import { ArtworkLinkGridCard } from "@/components/links/relatedLinkGridCards";
import { DashboardGridCardActions } from "@/components/table/DashboardGridCardActions";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import DashboardSelectionBar from "@/components/table/DashboardSelectionBar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import {
  artworkColumnsFormatted,
  getArtworkAdvancedSearchConfig,
} from "@/config/dashboard/artwork";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import { useAdditionalFilters } from "@/context/additional-filters/AdditionalFiltersContext";
import { useAuth } from "@/context/auth/AuthContext";
import { useDashboardActionsColumn } from "@/hooks/useDashboardActionsColumn";
import { toast } from "@/hooks/use-toast";
import useArtworkSet from "@/hooks/useArtworkSet";
import { useDashboardListScrollContext } from "@/hooks/useDashboardListScrollContext";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { useDashboardRowSelection } from "@/hooks/useDashboardRowSelection";
import { Artwork } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import DuplicateArtworkDialog from "@/components/artwork/DuplicateArtworkDialog";
import { ACTION_I18N_KEYS } from "@/components/table/action-i18n-keys";
import type { TableRowAction } from "@/components/table/TableRowActionsMenu";
import { useTranslation } from "react-i18next";
import { AdvancedSearchConfig } from "@/config/forms/advanced-search/advanced-search-fields-def";
import {
  ACTIONS_COLUMN_ID,
  ensureActionsColumnLast,
} from "@/components/table/table-actions-column";

const STORAGE_KEY = "artwork-table-preferences";

const flatVisibleArtworkColumns = [
  ...artworkColumnsFormatted.flatMap((column: ColumnFormatted) =>
    column.visible ? column.key : [],
  ),
  ACTIONS_COLUMN_ID,
];

const ArtworkDashboard = () => {
  const { t } = useTranslation();
  const { instanceData, user } = useAuth();
  const isReader = user?.role === "reader";
  const [duplicateSource, setDuplicateSource] = useState<
    { id: string; numero?: string } | undefined
  >(undefined);

  const artworkAdvancedSearchConfig: AdvancedSearchConfig =
    getArtworkAdvancedSearchConfig(instanceData);

  const { mutateAsync: deleteEnsembles } = useDeleteEnsembles();
  const filters = useAdditionalFilters();

  const { artworkSet, isInArtworkSet } = useArtworkSet();

  const {
    fetchParams,
    filterStatus,
    setFilterStatus,
    pagination,
    setPagination,
    countByStatus,
    currentCount,
  } = useDashboardParams<Artwork>({
    resourceName: "artwork",
    resourceCounter: getCountByStatus,
    statusField: "oeuvresPublicationStatus",
  });

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return ensureActionsColumnLast(order || flatVisibleArtworkColumns);
    }
    return flatVisibleArtworkColumns;
  });

  const [editArtworkSetOpen, setEditArtworkSetOpen] = useState(false);
  const [deleteSetOpen, setDeleteSetOpen] = useState(false);
  const { mutateAsync: updateArtworkSet } = useUpdateTemporarySet();
  const { mutateAsync: removeArtworksFromEnsembles } =
    useRemoveArtworksFromEnsembles();

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...artworkColumnsFormatted.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...artworkColumnsFormatted.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
        [ACTIONS_COLUMN_ID]: true,
      };
    },
  );

  const { data, isFetching } = useGetAllArtworks(fetchParams);
  const {
    listKey,
    navigateToFiche,
    entityIds,
    isLoading: listScrollLoading,
    listPagination,
  } = useDashboardListScrollContext(data, pagination, isFetching, STORAGE_KEY);

  const buildArtworkAdditionalActions = useCallback(
    (item: Artwork): TableRowAction[] =>
      isReader
        ? []
        : [
            {
              id: "duplicate",
              label: t(ACTION_I18N_KEYS.duplicate),
              icon: Copy,
              onSelect: () =>
                setDuplicateSource({
                  id: item.id,
                  numero: item.oeuvresDescriptionNumeroEnregistrement,
                }),
            },
          ],
    [isReader, t],
  );

  const actionsColumn = useDashboardActionsColumn<Artwork>({
    documentType: "artwork",
    publicationStatusField: "oeuvresPublicationStatus",
    onEdit: (row) =>
      navigateToFiche(`/edit/artwork/${row.id}`, row.id),
    buildAdditionalActions: buildArtworkAdditionalActions,
  });

  const columns: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork", t, instanceData),
      actionsColumn,
    ];
  }, [t, instanceData, actionsColumn]);

  const handleRowClick = (row: Row<Artwork>) => {
    navigateToFiche(`/edit/artwork/${row.original.id}`, row.original.id);
  };

  const handleDeleteSet = async (id: string) => {
    try {
      await deleteEnsembles(id);
      setDeleteSetOpen(false);
      filters?.setSetId(undefined);
      toast({
        title: t("ensembleDeleted"),
        variant: "success",
        description: t("ensembleDeletedDescription"),
      });
    } catch (error) {
      toast({
        title: t("error"),
        variant: "error",
        description: t("errorDescription"),
      });
      console.error(error);
    }
  };

  const pageTitle = artworkSet
    ? artworkSet.ensemblesDescriptionNomEnsemble
    : t("dashboardTitleArtworks");

  const {
    selectionState,
    setSelectionState,
    dataTableSelection,
    clearSelection,
    isSelected,
    setRowSelected,
  } = useDashboardRowSelection<Artwork>({
    getAllRowIds: () => getAllIds(fetchParams.where),
  });

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{pageTitle}</p>
        <GlobalSearch resource="artworks" config={artworkAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          {isInArtworkSet && (
            <Button
              variant="outline"
              className="flex flex-row items-center gap-2"
              onClick={() => setEditArtworkSetOpen(true)}
            >
              <Pencil size={16} />
              {t("updateEnsemble")}
            </Button>
          )}
          {isInArtworkSet && (
            <Button
              variant="outline"
              className="flex flex-row items-center gap-2"
              onClick={() => {
                if (artworkSet?.artworkCount === 0) {
                  handleDeleteSet(artworkSet.id);
                } else {
                  setDeleteSetOpen(true);
                }
              }}
            >
              <Trash2 size={16} />
            </Button>
          )}
          <AdvancedSearch
            config={artworkAdvancedSearchConfig}
            resource="artworks"
          />
          <AdvancedSort config={artworkAdvancedSearchConfig} />
          <NewArtworkJourney
            isArtmatchEnabled={!instanceData?.hideArtMatchBO}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DashboardListGridLayout
          storageKey={STORAGE_KEY}
          defaultView="grid"
          listKey={listKey}
          entityIds={entityIds}
          isLoading={listScrollLoading}
          listPagination={listPagination}
          setListPagination={setPagination}
          listTable={
            <DataTable
              onRowClick={handleRowClick}
              getListItemId={(row) => row.original.id}
              pagination={{
                pagination: pagination,
                setPage: setPagination,
                totalSize: currentCount,
              }}
              columns={columns}
              data={data ?? []}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
              }}
              selection={dataTableSelection}
            />
          }
          gridContent={
            <>
              {(data ?? []).map((item) => (
                <GridCardWithActions
                  key={item.id}
                  selected={isSelected(item.id)}
                  onSelectedChange={(checked) =>
                    setRowSelected(item.id, checked)
                  }
                  actions={
                    <DashboardGridCardActions
                      documentType="artwork"
                      item={item}
                      publicationStatusField="oeuvresPublicationStatus"
                      onEdit={(row) =>
                        navigateToFiche(`/edit/artwork/${row.id}`, row.id)
                      }
                      buildAdditionalActions={buildArtworkAdditionalActions}
                    />
                  }
                >
                  <ArtworkLinkGridCard
                    artwork={item}
                    t={t}
                    onTextClick={() =>
                      navigateToFiche(`/edit/artwork/${item.id}`, item.id)
                    }
                  />
                </GridCardWithActions>
              ))}
            </>
          }
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
        />
        <DashboardSelectionBar
          documentType="artwork"
          selectionState={selectionState}
          setSelectionState={setSelectionState}
        >
          {(selectedIds) => (
            <>
              <ArtworkPrintDrawer selectedArtworksIds={selectedIds} />

              {!isInArtworkSet && (
                <CreateArtworkSet
                  selectedArtworksIds={selectedIds}
                  resetSelectedArtworks={clearSelection}
                />
              )}
              {artworkSet && isInArtworkSet && (
                <Button
                  onClick={() =>
                    removeArtworksFromEnsembles({
                      ensembleIds: [artworkSet.id],
                      artworkIds: selectedIds,
                    })
                  }
                  variant="destructive"
                  className="flex flex-row gap-2 items-center"
                >
                  <Trash2 size={16} />
                  {t("removeArtworksFromSet")}
                </Button>
              )}
            </>
          )}
        </DashboardSelectionBar>

        {artworkSet && isInArtworkSet && (
          <NewArtworkSetModal
            open={editArtworkSetOpen}
            onOpenChange={setEditArtworkSetOpen}
            setToUpdate={artworkSet}
            onSubmit={(data) => {
              updateArtworkSet({ id: artworkSet.id, data });
              setEditArtworkSetOpen(false);
            }}
          />
        )}

        {artworkSet && (
          <DeleteSetModal
            open={deleteSetOpen}
            onOpenChange={setDeleteSetOpen}
            onSubmit={() => handleDeleteSet(artworkSet.id)}
          />
        )}

        <DuplicateArtworkDialog
          open={!!duplicateSource}
          onOpenChange={(open) => {
            if (!open) setDuplicateSource(undefined);
          }}
          sourceArtworkId={duplicateSource?.id}
          sourceNumeroEnregistrement={duplicateSource?.numero}
          onDuplicateSuccess={(created) => {
            setDuplicateSource(undefined);
            navigateToFiche(`/edit/artwork/${created.id}`, created.id);
          }}
        />
      </div>
    </div>
  );
};

export default ArtworkDashboard;
